import React, { useEffect, useState } from "react";
import { Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import BackgroundImage from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useFetchDataDetail, usePostDetail } from "@hooks/useFetchDetail";
import AnimationFadeIn from "@components/AnimationFadeIn";
import AssociationTableCst from "@components/AssociationTableCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { RolesDigitedEnum } from "@context/auth/types";
import { useAuth } from "@context/auth";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";

const CorporateAssociationDashboard: React.FC = () => {
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    key?: string | number;
  }>();
  const { dashboardId, id = "1" } = useParams();
  const { user } = useAuth();
  const isPO = user?.roles.includes(RolesDigitedEnum.PRODUCT_OWNER);
  const [corporateAssociation, setCorporateAssociation] = useState<any>(null);
  const [showTable, setShowTable] = useState(false);
  const { t } = useTranslation(["activities-digited-page", "toast-message"]);
  const navigate = useNavigate();
  const { dataDetail, isLoading } = useFetchDataDetail(
    `${ServicesURL.getAssociations}?dashboardAssociationType=CORPORATES&dashboardId=${dashboardId}&corporateId=${id}`,
    PagesID["dashboard.corporate-association"],
  );

  const { trigger } = usePostDetail(ServicesURL.associateCorporate);

  const onCorporateAssociation = async (selected: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToCorporateAssociation: any = Object.keys(selected).map(
      (key: string) => {
        if (key.includes("||")) {
          return {
            id: key.split("||")[0],
            label: key.split("||")[1],
          };
        }
      },
    );

    const dataDetailFormatted = dataDetail.map((corp: any) => {
      return {
        corporateId: Number(corp.id),
        corporateName: corp.label,
      };
    });

    const corporates = objToCorporateAssociation.map((corp: any) => {
      return {
        corporateId: Number(corp.id),
        corporateName: corp.label,
      };
    });

    const idsExist = dataDetailFormatted.map((item: any) => item.corporateId);
    const idsAssociated = corporates.map((item: any) => item.corporateId);

    const idsToAdd = corporates.filter(
      (item: any) => !idsExist.includes(item.corporateId),
    );

    const idsToDelete = dataDetailFormatted.filter(
      (item: any) => !idsAssociated.includes(item.corporateId),
    );

    const toSend: any = [...idsToAdd, ...idsToDelete];

    const objToSend: any = {
      corporates: toSend,
      dashboardId,
    };

    try {
      await trigger(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      console.error(error);
      setOpenToast(undefined);
    }
  };

  useEffect(() => {
    if (isLoading === undefined || !dataDetail) return;

    if (!dataDetail.length) {
      setCorporateAssociation([]);
      setShowTable(true);
      return;
    }

    const baseUrl = `${ServicesURL.getDashboard}?getUsersAndRoles=true&id=${dashboardId}${isPO ? "" : `&userId=${user?.id_user}`}`;

    Promise.all(
      dataDetail.map((element: any) =>
        fetcher<any>(`${baseUrl}&corporateId=${element.id}`, MethodType.GET),
      ),
    )
      .then((results) => {
        const selected = dataDetail.map((data: any, idx: number) => {
          const detail = results[idx]?.output?.[0];
          const isDisabled =
            detail?.associatedUsersNumber > 0 ||
            detail?.associatedRoles?.length > 0;
          return {
            id: data.id,
            name: data.label,
            disabled: isDisabled,
          };
        });
        setCorporateAssociation(selected);
        setShowTable(true);
      })
      .catch(console.error);
  }, [dataDetail, isLoading]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-activities.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("corporate-association.hero-title")}`}
          description={t("association.hero-description-domain")}
        />
        {!!showTable && (
          <AssociationTableCst
            association={corporateAssociation}
            setAssociation={null}
            callBackAssociation={onCorporateAssociation}
            setCloseAssociation={null}
            service={`${ServicesURL.corporate}?status=ACTIVE`}
            pageID={PagesID.corporate}
            title={""}
            enableMultiRowSelection={true}
            customBackAction={() => navigate(-1)}
            labelKey="name"
          />
        )}
        <ModalConfirmsCst
          open={!!openToast}
          title={t("hero-title-add")}
          description={""}
          onCallBackClosed={() => {
            setOpenToast(undefined);
          }}
          status={openToast}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default CorporateAssociationDashboard;
